import {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {GridItem, useDisclosure, useToast} from '@chakra-ui/react'
import {useLocation} from 'react-router'
import {useTranslation} from 'react-i18next'
import {useRails} from '../../contexts/rails'
import {refreshStepperData} from '../CashinStepper/useStepperData'
import ExitConfirmationDialog from '../ExitConfirmationDialog'
import useSubmitPayoutAccountSupportRequest from './useSubmitPayoutAccountSupportRequest'
import useMatchReconciliationAccount from './useMatchReconciliationAccount'
import {
  InformationScreen,
  YodleeIntegrationScreen,
  PayoutAccountFallbackScreen,
  GetHelpScreen,
  SupportRequestReceivedScreen,
  SelectPayoutAccountScreen,
  ConfirmationScreen,
  ReconciliationAccountScreen,
} from './screens'
import StepFlowModal from '../StepFlowModal'
import useStepNavigation, {isLastStep} from './useStepNavigation'

const payoutAccountConnectedEndpoint = (supplierId) =>
  `/api/suppliers/${supplierId}/banking_providers/payout_account_connected`

const ConnectPayoutAccountModals = ({isOpen, onClose}) => {
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure()

  const {
    organisation: {currentSupplier},
  } = useRails()
  const location = useLocation()
  const {t} = useTranslation('connectPayoutAccount')

  const getCurrentStepFromPath = useCallback(() => {
    const pathParts = location.pathname.split('/')
    const lastPart = pathParts[pathParts.length - 1]

    if (lastPart === 'connect_payout_account' || !lastPart) {
      return 'information'
    }

    return lastPart.replace(/-/g, '_')
  }, [location.pathname])

  const [step, setStep] = useState(getCurrentStepFromPath())
  const [displayLearnMore, setDisplayLearnMore] = useState(false)
  const [connectedPayoutAccount, setConnectedPayoutAccount] = useState(null)
  const [isManualSubmission, setIsManualSubmission] = useState(false)
  const [manualDetails, setManualDetails] = useState(null)
  const toast = useToast()
  const {submit: submitSupportRequest} = useSubmitPayoutAccountSupportRequest()
  const {match: matchReconciliationAccount} = useMatchReconciliationAccount()

  // Update step when path changes
  useEffect(() => {
    setStep(getCurrentStepFromPath())
  }, [location.pathname, getCurrentStepFromPath])

  const {
    goToNextStep,
    goToReconciliationAccount,
    goToConfirmation,
    goToSupportRequestReceived,
    goToDashboard,
    onYodleeFailure,
    onTryAgain,
    goToGetHelp,
    goToConnectExpenseAccount,
  } = useStepNavigation(step)

  // Instead of doing navigation, set displayLearnMore to false
  const goToInformationScreen = () => {
    setDisplayLearnMore(false)
  }

  // The support request is only created once the manual flow finishes, so nothing
  // is recorded (and the onboarding step never shows "in progress") until then.
  const submitManualDetails = async (details) => {
    try {
      await submitSupportRequest(details)
      goToSupportRequestReceived()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || t('modal.getHelp.genericError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // On submitting the manual form, try to match the entered account to an
  // accounting account. On a match the reconciliation account is set for them and
  // the reconciliation screen is skipped; otherwise they select it themselves.
  const startManualSubmission = async (details) => {
    setManualDetails(details)
    setConnectedPayoutAccount({
      bank_account_name: details.accountName,
      account_number: details.accountNumber,
    })
    setIsManualSubmission(true)

    let matchedId = null
    try {
      const result = await matchReconciliationAccount({
        bsb: details.bsb,
        accountNumber: details.accountNumber,
      })
      matchedId = result?.bank_account_id
    } catch {
      matchedId = null
    }

    if (matchedId) {
      await submitManualDetails(details)
    } else {
      goToReconciliationAccount()
    }
  }

  const completeManualSubmission = () => submitManualDetails(manualDetails)

  const handleClose = () => {
    if (step === 'confirmation' || step === 'support_request_received') {
      // A submitted support request leaves the payout step awaiting review, so
      // refetch the stepper to reflect that without a manual page refresh.
      refreshStepperData()
      onClose()
    } else if (isLastStep(step)) {
      onClose()
    } else {
      onAlertOpen()
    }
  }

  const handleConfirmClose = () => {
    onAlertClose()
    goToDashboard()
  }

  const getModalStep = () => {
    switch (step) {
      case 'information':
        return (
          <InformationScreen
            goToNextStep={goToNextStep}
            displayLearnMore={displayLearnMore}
            setDisplayLearnMore={setDisplayLearnMore}
          />
        )
      case 'yodlee':
        return (
          <YodleeIntegrationScreen
            onSuccess={goToNextStep}
            onFailure={onYodleeFailure}
            onBankNotListed={goToGetHelp}
            connectedEndpoint={payoutAccountConnectedEndpoint(
              currentSupplier.id,
            )}
          />
        )
      case 'payout_account':
        return (
          <SelectPayoutAccountScreen
            goToConfirmation={goToConfirmation}
            goToReconciliationAccount={(account) => {
              setConnectedPayoutAccount(account)
              setIsManualSubmission(false)
              goToReconciliationAccount()
            }}
            onClose={handleClose}
          />
        )
      case 'failed':
        return (
          <PayoutAccountFallbackScreen
            goToNextStep={goToNextStep}
            onTryAgain={onTryAgain}
          />
        )
      case 'get_help':
        return (
          <GetHelpScreen
            onSubmitted={startManualSubmission}
            onCancel={handleClose}
          />
        )
      case 'support_request_received':
        return <SupportRequestReceivedScreen onDone={handleClose} />
      case 'reconciliation_account':
        return (
          <ReconciliationAccountScreen
            goToNextStep={
              isManualSubmission ? completeManualSubmission : goToNextStep
            }
            onClose={handleClose}
            connectedPayoutAccount={connectedPayoutAccount}
          />
        )
      case 'confirmation':
        return (
          <ConfirmationScreen
            goToSetupExpenseAccount={goToConnectExpenseAccount}
          />
        )
      default:
        return (
          <InformationScreen
            goToNextStep={goToNextStep}
            displayLearnMore={displayLearnMore}
            setDisplayLearnMore={setDisplayLearnMore}
          />
        )
    }
  }

  const modalStep = getModalStep()

  return (
    <>
      <StepFlowModal
        isOpen={isOpen}
        handleClose={handleClose}
        introductionTitle={t('introduction.title')}
        introductionSubtitle={t('introduction.subtitle')}
        displayHeader={step !== 'yodlee'}
        goToPreviousStep={goToInformationScreen}
        goToPreviousStepEnabled={displayLearnMore}
      >
        <GridItem colSpan={2} position="relative">
          {modalStep}
        </GridItem>
      </StepFlowModal>
      <ExitConfirmationDialog
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        onConfirmExit={handleConfirmClose}
      />
    </>
  )
}

ConnectPayoutAccountModals.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ConnectPayoutAccountModals
